import { getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const openDialogs = [];
let previousOverflow = '';

export function getOpenV2Dialog() {
  for (let index = openDialogs.length - 1; index >= 0; index--) {
    if (openDialogs[index].open) return openDialogs[index];
  }
  return null;
}

// Keep boundary Tab inside the dialog instead of moving focus to browser chrome.
export function handleDialogTab(event, dialog) {
  if (event.key !== 'Tab' || event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey
    || !dialog?.open) return;

  const controls = [...dialog.querySelectorAll('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex], [contenteditable="true"]')]
    .filter(control => control.tabIndex >= 0 && !control.matches(':disabled') && !control.closest('[inert]')
      && control.getClientRects().length && getComputedStyle(control).visibility === 'visible')
    .sort((a, b) => (a.tabIndex || Number.MAX_SAFE_INTEGER) - (b.tabIndex || Number.MAX_SAFE_INTEGER));
  const first = controls[0];
  const last = controls.at(-1);
  const current = document.activeElement;
  const target = !controls.length ? dialog
    : event.shiftKey && (current === first || current === dialog) ? last
      : !event.shiftKey && (current === last || current === dialog) ? first : null;
  if (!target) return;
  event.preventDefault();
  target.focus();
}

export function useV2Dialog(props, { emit }) {
  const dialog = ref(null);
  const titleId = `v2-dialog-${getCurrentInstance().uid}-title`;
  let activeDialog = null;
  let opener = null;
  let backdropPressed = false;

  function release() {
    const index = openDialogs.indexOf(activeDialog);
    if (index === -1) return;
    const wasTop = index === openDialogs.length - 1;
    openDialogs.splice(index, 1);
    activeDialog = null;
    if (!openDialogs.length) document.body.style.overflow = previousOverflow;
    if (!wasTop) return;

    const remaining = openDialogs.at(-1) || document.querySelector('dialog[open]');
    const canRestore = opener?.isConnected && opener !== document.body && opener !== document.documentElement
      && !opener.matches(':disabled') && !opener.closest('[inert]')
      && !opener.closest('dialog:not([open])') && opener.getClientRects().length
      && getComputedStyle(opener).visibility === 'visible' && (!remaining || remaining.contains(opener));
    const target = canRestore ? opener : remaining || document.querySelector('main') || document.body;
    const needsTabindex = !target.hasAttribute('tabindex') && target.tabIndex < 0;
    if (needsTabindex) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (needsTabindex) target.removeAttribute('tabindex');
    opener = null;
  }

  function sync() {
    if (!props.show) {
      close();
      return;
    }
    const element = dialog.value;
    if (!element) return;
    if (!element.open) {
      opener = document.activeElement;
      element.showModal();
      activeDialog = element;
      if (!openDialogs.length) previousOverflow = document.body.style.overflow;
      openDialogs.push(element);
      document.body.style.overflow = 'hidden';
    }
  }

  function close() {
    if (activeDialog?.open) activeDialog.close();
    release();
  }

  function requestClose() {
    if (props.show) emit('close');
  }

  function onNativeClose() {
    // A queued close event from a previous opening must not dismiss a reopened dialog.
    if (activeDialog?.open) return;
    release();
    requestClose();
    // An owner that refuses the close (a PAC call is pending) keeps show true while
    // the element is now closed; without this, reopening (true -> true) never fires.
    nextTick(() => {
      if (props.show && dialog.value && !dialog.value.open) sync();
    });
  }

  function outside(event) {
    if (event.target !== dialog.value) return false;
    const rect = dialog.value.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right
      || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  function onBackdropPointerDown(event) {
    backdropPressed = outside(event);
  }

  function onBackdropClick(event) {
    if (backdropPressed && outside(event)) requestClose();
    backdropPressed = false;
  }

  function handleTab(event) {
    if (openDialogs.at(-1) === activeDialog) handleDialogTab(event, activeDialog);
  }

  watch(() => props.show, sync, { flush: 'post' });
  onMounted(sync);
  onBeforeUnmount(close);

  return { dialog, titleId, requestClose, onNativeClose, onBackdropPointerDown, onBackdropClick, handleTab };
}
